import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, DollarSign, CheckCircle, ChevronRight, ArrowLeft, Building } from 'lucide-react';
import Stepper from '../components/ui/Stepper';
import api from '../api/apiClient';

interface PaymentDetails {
  nextPaymentDue: string;
  nextPaymentAmount: number;
  propertyAddress: string;
  leaseId: number;
  overdueAmount: number;
  autopayEnabled: boolean;
}

interface PaymentMethod {
  id: string;
  type: string;
  label: string;
  accountMasked?: string;
  bankName?: string;
  isDefault?: boolean;
}

const STEPS = ['Review', 'Method', 'Confirm'];

export default function PayRent() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  const [paymentId, setPaymentId] = useState<number | null>(null);
  const [amount, setAmount] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);

  useEffect(() => { loadPaymentData(); }, []);

  const loadPaymentData = async () => {
    setLoading(true);
    setError('');
    try {
      // 1. Get next rent due — includes leaseId, amount, date from renter dashboard
      const nextRent = await api.get<any>('/renter/dashboard/next-rent');
      if (!nextRent || !nextRent.leaseId) {
        setError('No active lease found. Please contact your agent.');
        return;
      }
      const leaseId: number = nextRent.leaseId;

      // 2. Get payment options — saved methods + next unpaid payment details
      const options = await api.get<any>(`/renter/payments/lease/${leaseId}/options`);
      const nextPayment = options.nextPayment;
      setPaymentId(nextPayment?.id ?? null);

      const dueAmount: number = nextPayment?.amount ?? nextRent.amount ?? options.rentAmount ?? 0;
      setPaymentDetails({
        nextPaymentDue: nextPayment?.dueDate || nextRent.date,
        nextPaymentAmount: dueAmount,
        propertyAddress: options.propertyAddress || nextRent.propertyAddress || '',
        leaseId,
        overdueAmount: 0,
        autopayEnabled: false,
      });

      const methods: PaymentMethod[] = options.savedPaymentMethods || [];
      setPaymentMethods(methods);
      if (methods.length > 0) setSelectedMethod(methods[0].id);
      setAmount(dueAmount.toFixed(2));
    } catch (err: any) {
      setError(err?.message || 'Failed to load payment details');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!selectedMethod) { setError('Please select a payment method'); return; }
    if (!paymentId) { setError('No outstanding payment found. Please refresh.'); return; }
    if (!amount || parseFloat(amount) <= 0) { setError('Please enter a valid amount'); return; }
    setProcessing(true);
    setError('');
    try {
      await api.post('/renter/payments/pay', {
        paymentId,
        paymentMethodId: selectedMethod,
      });
      setSuccess(true);
    } catch (err: any) {
      setError(err?.message || 'Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const fmt = (d: string) =>
    new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });

  const methodLabel = (m: PaymentMethod) => m.label || m.type;

  const selected = paymentMethods.find(m => m.id === selectedMethod);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-forest-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="px-6 py-10 max-w-lg mx-auto">
        <div
          className="rounded-16 p-8 text-white text-center mb-6"
          style={{ background: 'linear-gradient(135deg, var(--forest-900), var(--forest-700))' }}
        >
          <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={32} className="text-white" />
          </div>
          <h2 className="text-2xl font-semibold mb-1">Payment Sent!</h2>
          <p className="text-white/70 text-[13px]">Your rent payment has been submitted.</p>
        </div>
        <div className="bg-card rounded-12 border border-ink-200 shadow-xs p-5 space-y-3 mb-5">
          <div className="flex justify-between text-[13px]">
            <span className="text-ink-600">Amount paid</span>
            <span className="font-semibold text-ink-900 tabular-nums">${parseFloat(amount).toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-[13px] border-t border-ink-200 pt-3">
            <span className="text-ink-600">Method</span>
            <span className="text-ink-900">{selected ? methodLabel(selected) : '—'}</span>
          </div>
          <div className="flex justify-between text-[13px]">
            <span className="text-ink-600">Property</span>
            <span className="text-ink-900 truncate max-w-[180px]">{paymentDetails?.propertyAddress}</span>
          </div>
        </div>
        <div className="bg-forest-50 border border-forest-200 rounded-10 p-4 text-[12px] text-forest-900 mb-6">
          <strong>Next payment:</strong> {paymentDetails ? fmt(paymentDetails.nextPaymentDue) : '—'} · ${paymentDetails?.nextPaymentAmount.toFixed(2)}
        </div>
        <button
          onClick={() => navigate('/renter/dashboard')}
          className="w-full py-3 rounded-10 bg-forest-900 text-white font-semibold text-[14px] hover:bg-forest-800 transition-colors"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="px-6 py-6 max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => step === 0 ? navigate(-1) : setStep(s => s - 1)}
          className="w-8 h-8 rounded-8 border border-ink-200 flex items-center justify-center text-ink-700 hover:bg-ink-100 transition-colors"
        >
          <ArrowLeft size={16} />
        </button>
        <h1 className="text-xl font-semibold text-ink-900">Pay Rent</h1>
      </div>

      {/* Stepper */}
      <div className="bg-card rounded-12 border border-ink-200 shadow-xs p-5">
        <Stepper steps={STEPS} current={step} />
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 rounded-10 bg-danger-50 border border-red-200 text-[13px] text-danger-700">
          {error}
        </div>
      )}

      {/* Step 0: Review */}
      {step === 0 && (
        <div className="space-y-4">
          {/* Property card */}
          <div
            className="rounded-16 p-5 text-white"
            style={{ background: 'linear-gradient(135deg, var(--forest-900), var(--forest-700))' }}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-9 h-9 rounded-10 bg-white/15 flex items-center justify-center flex-none">
                <Building size={18} />
              </div>
              <div>
                <p className="text-white/60 text-[11px] uppercase tracking-wider">Property</p>
                <p className="text-sm font-medium mt-0.5">{paymentDetails?.propertyAddress}</p>
              </div>
            </div>
            <div className="flex gap-8">
              <div>
                <p className="text-white/60 text-[11px] uppercase tracking-wider mb-0.5">Amount Due</p>
                <p className="text-3xl font-semibold tabular-nums">
                  ${paymentDetails?.nextPaymentAmount.toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-white/60 text-[11px] uppercase tracking-wider mb-0.5">Due Date</p>
                <p className="text-lg font-medium">
                  {paymentDetails ? fmt(paymentDetails.nextPaymentDue) : '—'}
                </p>
              </div>
            </div>
          </div>

          {/* Overdue notice */}
          {(paymentDetails?.overdueAmount ?? 0) > 0 && (
            <div className="p-4 rounded-10 bg-danger-50 border border-red-200 text-[13px] text-danger-700">
              <strong>Overdue balance:</strong> ${paymentDetails?.overdueAmount.toFixed(2)} — consider paying all outstanding amounts.
            </div>
          )}

          {/* Amount selector */}
          <div className="bg-card rounded-12 border border-ink-200 shadow-xs p-5">
            <label className="block text-[13px] font-medium text-ink-700 mb-2">Amount to pay</label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-500 text-lg">$</span>
              <input
                type="number" step="0.01" min="0"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                className="w-full pl-8 pr-4 py-3 border border-ink-200 rounded-10 text-lg font-semibold text-ink-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-forest-900 focus:border-transparent"
              />
            </div>
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => setAmount((paymentDetails?.nextPaymentAmount ?? 0).toFixed(2))}
                className="px-3 py-1.5 text-[12px] rounded-8 bg-ink-100 text-ink-700 hover:bg-ink-200 transition-colors font-medium"
              >
                Next due ${paymentDetails?.nextPaymentAmount.toFixed(2)}
              </button>
              {(paymentDetails?.overdueAmount ?? 0) > 0 && (
                <button
                  onClick={() => setAmount(((paymentDetails?.overdueAmount ?? 0) + (paymentDetails?.nextPaymentAmount ?? 0)).toFixed(2))}
                  className="px-3 py-1.5 text-[12px] rounded-8 bg-danger-50 text-danger-700 hover:bg-red-100 transition-colors font-medium"
                >
                  Pay all ${((paymentDetails?.overdueAmount ?? 0) + (paymentDetails?.nextPaymentAmount ?? 0)).toFixed(2)}
                </button>
              )}
            </div>
          </div>

          <button
            onClick={() => setStep(1)}
            disabled={!amount || parseFloat(amount) <= 0}
            className="w-full py-3 rounded-10 bg-forest-900 text-white font-semibold text-[14px] hover:bg-forest-800 disabled:opacity-40 transition-colors flex items-center justify-center gap-2"
          >
            Continue <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Step 1: Method */}
      {step === 1 && (
        <div className="space-y-4">
          <div className="bg-card rounded-12 border border-ink-200 shadow-xs p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[13px] font-semibold text-ink-700 uppercase tracking-wider">Payment Method</h2>
              <button
                onClick={() => navigate('/renter/payment-methods')}
                className="text-[12px] text-forest-900 font-medium hover:underline"
              >
                Manage
              </button>
            </div>
            <div className="space-y-2">
              {paymentMethods.map(method => (
                <label
                  key={method.id}
                  className={`flex items-center gap-3 p-4 rounded-10 border-2 cursor-pointer transition-all ${
                    selectedMethod === method.id
                      ? 'border-forest-900 bg-forest-50'
                      : 'border-ink-200 hover:border-ink-300'
                  }`}
                >
                  <input
                    type="radio" name="method" value={method.id}
                    checked={selectedMethod === method.id}
                    onChange={() => setSelectedMethod(method.id)}
                    className="w-4 h-4 accent-forest-900"
                  />
                  <div className="w-8 h-8 rounded-8 bg-ink-100 flex items-center justify-center flex-none">
                    <CreditCard size={16} className="text-ink-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-[13px] font-medium text-ink-900">{methodLabel(method)}</p>
                    {method.isDefault && <p className="text-[11px] text-teal-600">Default</p>}
                  </div>
                  {selectedMethod === method.id && (
                    <CheckCircle size={16} className="text-forest-900 flex-none" />
                  )}
                </label>
              ))}
            </div>
          </div>

          <div className="p-4 rounded-10 bg-ink-50 border border-ink-200 text-[12px] text-ink-600">
            Payments processed securely. BPAY reference numbers will be emailed to you. Card payments may include a 0.75% surcharge.
          </div>

          <button
            onClick={() => setStep(2)}
            disabled={!selectedMethod}
            className="w-full py-3 rounded-10 bg-forest-900 text-white font-semibold text-[14px] hover:bg-forest-800 disabled:opacity-40 transition-colors flex items-center justify-center gap-2"
          >
            Review Payment <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Step 2: Confirm */}
      {step === 2 && (
        <div className="space-y-4">
          <div className="bg-card rounded-12 border border-ink-200 shadow-xs p-5 space-y-3">
            <h2 className="text-[13px] font-semibold text-ink-700 uppercase tracking-wider mb-2">Review Your Payment</h2>
            {[
              { label: 'Property', value: paymentDetails?.propertyAddress },
              { label: 'Amount',   value: `$${parseFloat(amount).toFixed(2)}`, mono: true },
              { label: 'Method',   value: selected ? methodLabel(selected) : '—' },
              { label: 'Due date', value: paymentDetails ? fmt(paymentDetails.nextPaymentDue) : '—' },
            ].map(r => (
              <div key={r.label} className="flex justify-between py-2.5 border-b border-ink-200 last:border-0">
                <span className="text-[13px] text-ink-600">{r.label}</span>
                <span className={`text-[13px] font-medium text-ink-900 ${r.mono ? 'tabular-nums' : ''}`}>{r.value}</span>
              </div>
            ))}
          </div>

          <button
            onClick={handlePayment}
            disabled={processing}
            className="w-full py-3 rounded-10 bg-forest-900 text-white font-semibold text-[14px] hover:bg-forest-800 disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
          >
            {processing ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <DollarSign size={16} />
                Confirm Payment
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
